import React, { useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { SectionHeading } from "../SectionHeading";
import { type DialogVideo, VideoDialog } from "../VideoDialog";
import { testimonials } from "../../data/portfolio";

/** Fallback mark for anyone without a supplied portrait. */
function initials(name: string) {
  return name.split(" ").filter(Boolean).slice(0, 2).map(part => part[0]?.toUpperCase() ?? "").join("");
}
export function TestimonialsSection() {
  const [video, setVideo] = useState<DialogVideo | null>(null);
  const videoCount = testimonials.filter(item => item.videoUrl).length;
  return <section id="testimonials" aria-labelledby="testimonials-heading" className="border-t border-white/10 px-5 py-24 md:px-8 md:py-32" data-id="element-278" data-mp="2bod8xk7h5tnf">
      <div className="mx-auto max-w-[1480px]" data-id="element-279" data-mp="2zde9ymdza2io">
        <SectionHeading index="007" label="Community trust, testimonials" titleStart="What" accent="people" titleEnd="say" headingId="testimonials-heading" body="A few words from directors, producers, and design leads who have worked with Zeeshan directly. Quoted exactly as written, with video recommendations playing in full." data-id="element-280" data-mp="344wbpcnts74p" />

        <ul className="mt-16 gap-5 md:columns-2 lg:columns-3" data-id="element-281" data-mp="1ak5e8w65mmv8">
          {testimonials.map((item, index) => <li key={item.name} className="mb-5 break-inside-avoid" data-id="element-282" data-mp="1oaqxwn3fn0u6" data-mpi={index}>
              <motion.figure initial={{
            opacity: 0,
            y: 26
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-8%"
          }} transition={{
            duration: 0.6,
            delay: Math.min(index * 0.07, 0.35),
            ease: [0.16, 1, 0.3, 1]
          }} className="group relative h-full overflow-hidden rounded-[20px] border border-white/10 bg-[#0a0f0e] p-6 transition-[transform,border-color] duration-500 hover:-translate-y-1 hover:border-[#54f6c6]/40 md:p-7" data-id="element-283" data-mp="1od4tqy5xsh2s">
                <span aria-hidden="true" className="absolute inset-x-0 top-0 h-px scale-x-0 bg-gradient-to-r from-transparent via-[#54f6c6]/70 to-transparent transition-transform duration-500 group-hover:scale-x-100" data-id="element-284" data-mp="1yzdb8r9qmw35" />

                <div className="flex items-start justify-between" data-id="element-285" data-mp="27yi8lu9kcjdq">
                  <span aria-hidden="true" className="font-display text-3xl leading-none text-[#54f6c6]" data-id="element-286" data-mp="2cyw6ijhgacy1">“</span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-white/45" data-id="element-287" data-mp="2hy933vy4iydt">
                    {String(index + 1).padStart(2, "0")} / {String(testimonials.length).padStart(2, "0")}
                  </span>
                </div>

                <blockquote className="mt-5 text-sm leading-7 text-white/70 transition-colors duration-500 group-hover:text-white/90 md:text-[15px] md:leading-8" data-id="element-288" data-mp="2rpmx5o7b35zk">
                  {item.quote}
                </blockquote>

                <figcaption className="mt-7 flex items-end justify-between gap-4 border-t border-white/10 pt-5" data-id="element-289" data-mp="35ms9u71iz33s">
                  <span className="flex min-w-0 items-start gap-4" data-id="element-290" data-mp="3fr8r94unks9p">
                    <span className="relative grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-full border border-white/15 bg-white/[0.06]" data-id="element-291" data-mp="3arvt3nybzyrt">
                      {item.avatar ? <img src={item.avatar} alt={`Portrait of ${item.name}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-292" data-mp="31r7f2t8xtyex" /> : <span aria-hidden="true" className="font-mono text-[11px] tracking-[0.08em] text-white/60" data-id="element-293" data-mp="3oqc9y7pwr5no">
                          {initials(item.name)}
                        </span>}
                    </span>
                    <span className="min-w-0" data-id="element-294" data-mp="l4er0u7xhf19">
                      <span className="block text-sm font-medium text-white" data-id="element-295" data-mp="gasj1fcplzbs">{item.name}</span>
                      <span className="mt-2 block text-[13px] leading-6 text-white/60" data-id="element-296" data-mp="ssmbj7az9bg">{item.role}</span>
                    </span>
                  </span>

                  <button type="button" disabled={!item.videoUrl} onClick={() => {
                if (!item.videoUrl) return;
                setVideo({
                  url: item.videoUrl,
                  title: item.name,
                  subtitle: item.role,
                  aspect: item.videoAspect ?? "landscape"
                });
              }} data-cursor={item.videoUrl ? "Play" : undefined} title={item.videoUrl ? `Play video feedback from ${item.name}` : "Video feedback not published yet"} aria-label={item.videoUrl ? `Play video feedback from ${item.name}` : `Video feedback from ${item.name} is not available`} className={`relative grid h-12 w-12 shrink-0 place-items-center rounded-xl border transition-[transform,background-color,color,border-color] duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6] ${item.videoUrl ? "border-[#54f6c6]/60 text-[#54f6c6] hover:scale-105 hover:bg-[#54f6c6] hover:text-[#05100d]" : "cursor-not-allowed border-white/15 text-white/25"}`} data-id="element-297" data-mp="5qkhcm38vbjv">
                    <Play className="h-4 w-4 fill-current" aria-hidden="true" data-id="element-298" data-mp="2435rl2o7l7us" />
                    {item.videoUrl && <span aria-hidden="true" className="absolute inset-0 rounded-xl border border-[#54f6c6]/40 rec-pulse" data-id="element-299" data-mp="2j0eoebwa73tw" />}
                  </button>
                </figcaption>
              </motion.figure>
            </li>)}
        </ul>

        <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.16em] text-white/45" data-id="element-300" data-mp="3pgi9ajz2jedr">
          {String(testimonials.length).padStart(2, "0")} recommendations
          {videoCount > 0 ? ` · ${videoCount} on video` : ""}
        </p>
      </div>

      <VideoDialog video={video} onClose={() => setVideo(null)} data-id="element-301" data-mp="5ep1fwd2z1ve" />
    </section>;
}